import React, { useState } from "react";
import {
  RouterProps,
  Link as ReactRouterLink,
  useParams
} from "react-router-dom";
import {
  Center,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Button,
  FormErrorMessage,
  Alert,
  AlertIcon,
  AlertDescription,
  AlertTitle,
  Link,
  CloseButton
} from "@chakra-ui/react";
import { Formik, Form, Field, FieldProps } from "formik";
import * as Yup from "yup";
import { resetPassword } from "../../utils/auth-api";

type FormValuesProps = {
  password: string;
  confirmPassword: string;
};

interface ErrormessageType {
  message: string;
}

interface ErrorType {
  errors: Array<ErrormessageType>;
}

export const ResetPassword = (_props: RouterProps) => {
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { resetKey } = useParams<{ resetKey: string }>();
  return success ? (
    <Center height="100%">
      <Alert
        status="success"
        variant="subtle"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        height="200px"
        width="600px"
      >
        <AlertIcon boxSize="40px" mr={0} />
        <AlertTitle mt={4} mb={1} fontSize="lg">
          Password has been successfully updated
        </AlertTitle>
        <AlertDescription maxWidth="sm">
          Please{" "}
          <Link textDecoration="underline" as={ReactRouterLink} to="/">
            login
          </Link>{" "}
          with the new password
        </AlertDescription>
      </Alert>
    </Center>
  ) : (
    <Center height="100%">
      <Stack
        height="100%"
        spacing="10"
        width="400px"
        justifyContent="center"
        alignItems="center"
      >
        <Heading size="lg">Set New Password</Heading>
        {error ? (
          <Alert status="error">
            <AlertIcon />
            <AlertDescription>{error}</AlertDescription>
            <CloseButton
              position="absolute"
              right="8px"
              top="8px"
              onClick={() => setError(null)}
            />
          </Alert>
        ) : null}
        <Formik<FormValuesProps>
          initialValues={{
            password: "",
            confirmPassword: ""
          }}
          validationSchema={Yup.object({
            password: Yup.string()
              .matches(
                /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/,
                "Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and one special case Character"
              )
              .required("This is a required field."),
            confirmPassword: Yup.string()
              .oneOf([Yup.ref("password"), null], "Passwords must match")
              .required("This is a required field.")
          })}
          onSubmit={async values => {
            try {
              await resetPassword({ password: values.password, resetKey });
              setSuccess(true);
            } catch (e) {
              const event = e as ErrorType;
              if (event.errors.length > 0) {
                const error = event.errors[0];
                setError(error.message);
              }
            }
          }}
        >
          {() => {
            return (
              <Form style={{ width: "100%" }}>
                <Stack width="100%" spacing="8">
                  <Field name="password">
                    {({ field, form }: FieldProps) => (
                      <FormControl
                        isInvalid={
                          form.errors.password !== undefined &&
                          form.touched.password !== undefined
                        }
                        isRequired
                      >
                        <FormLabel htmlFor="password">Password</FormLabel>
                        <Input
                          {...field}
                          id="password"
                          type="password"
                          name="password"
                        />
                        <FormErrorMessage>
                          {form.errors.password}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Field name="confirmPassword">
                    {({ field, form }: FieldProps) => (
                      <FormControl
                        isInvalid={
                          form.errors.confirmPassword !== undefined &&
                          form.touched.confirmPassword !== undefined
                        }
                        isRequired
                      >
                        <FormLabel>Confirm Password</FormLabel>
                        <Input
                          {...field}
                          type="password"
                          id="confirmPassword"
                          name="confirmPassword"
                        />
                        <FormErrorMessage>
                          {form.errors.confirmPassword}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                  <Button type="submit" color="white" bg="#2a9df4" rounded="3">
                    Reset Password
                  </Button>
                </Stack>
              </Form>
            );
          }}
        </Formik>
      </Stack>
    </Center>
  );
};
